
import { DateTime, Duration, Settings } from "luxon";
import { DateLocale, LocaleString, TimeZoneString, UTC_TZ } from "../models/locale.model";
import { anyObject, getProp } from "./any.tools";
import { TsInterval } from "./interval.tools";
import { keyof } from "./object.tools";
import { MINUTES_IN_ONE_HOUR, SECONDS_IN_ONE_HOUR, SECONDS_IN_ONE_MINUTE } from "./time.tools";
import { DATE_UNITS, DAYS, DEFAULT_DATETIME_FORMAT, DEFAULT_DATE_FORMAT, IS_TS_DATE_PROPERTY, LOOSE_PARSE_OPTIONS, LooseParsingOptions, TimestampsSettings, TsDate, TsDateFormat, TsDateInterval, TsDateObjectValues, TsDateUnit, TsDuration, TsDurationParam, TsParam, TsSimpleDuration, UnixTimestamp, tsParamUts, tsValueOf } from "./timestamps.tools.definitions";
import { installOptimizedIANAZone } from "./timestamps.tools.tsdate.timezones";

/** 
 * @deprecated This function is not deprecated but most of the time you should not call it, it is called by AppService when setting locale
 */
export function setDefaultDateLocale(defaultDateLocale: DateLocale): void {
    if (TimestampsSettings.systemDateLocale == null) {
        // save system infos on first call
        TimestampsSettings.systemDateLocale = {
            dateLocale: Settings.defaultLocale,
            timeZone: Settings.defaultZone["name"] ?? String(Settings.defaultZone)
        };
    }

    Settings.defaultLocale = defaultDateLocale.dateLocale ?? TimestampsSettings.systemDateLocale.dateLocale;
    Settings.defaultZone = defaultDateLocale.timeZone ?? TimestampsSettings.systemDateLocale.timeZone;
    TimestampsSettings.defaultDateLocale = {
        dateLocale: Settings.defaultLocale,
        timeZone: Settings.defaultZone["name"] ?? String(Settings.defaultZone)
    };

    // Improve luxon performance
    installOptimizedIANAZone();
}

/**
 * @returns the current default date locale settings
 * * If the AppService was registered, it returns the TimestampsSettings.defaultDateLocale otherwise we read defaults from luxon Settings
 */
export function getDefaultDateLocale(): DateLocale {
    return TimestampsSettings.defaultDateLocale ?? { dateLocale: Settings.defaultLocale, timeZone: Settings.defaultZone.toString() };
}

export const frDays: ("lundi" | "mardi" | "mercredi" | "jeudi" | "vendredi" | "samedi" | "dimanche")[] = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
export const enDays: ("monday" | "tuesday" | "wednesday" | "thursday" | "friday" | "saturday" | "sunday")[] = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

/**
 * @returns the formatted day of week (1 => monday) for provided locale (for now only FR and EN)
 */
export function dayOfWeekToString(weekDay: DAYS, locale?: Partial<DateLocale>): string | undefined {
    const l = locale?.dateLocale ?? TimestampsSettings.defaultDateLocale?.dateLocale;
    if (l?.startsWith("fr")) {
        return frDays[weekDay - 1];
    }
    return enDays[weekDay - 1];
}

/**
 * Retrieve today date define for timestamps tools, in Date format
 * Can be override by overrideTodayDate in TsSettings
 * @returns {Date} todayDate
 */
export function getTodayDate(): TsDate {
    return beginOf(getNowDate(), "day");
}

/**
 * Retrieve today date define for timestamps tools, in Date format
 * Can be override by overrideTodayDate in TsSettings
 * @returns {Date} todayDate
 */
export function getNowDate(): TsDate {
    let nowDt = _td(DateTime.now());
    if (TimestampsSettings.overrideTodayDate) {
        nowDt = set(TimestampsSettings.overrideTodayDate, { secondOfDay: nowDt.secondOfDay });
    }
    return nowDt;
}

/**
 * Transform a timestamp in TsDate
 * @param ts Timestamp
 */
export function toDate(ts: UnixTimestamp | number, locale?: Partial<DateLocale>): TsDate {
    return _td(DateTime.fromSeconds(ts as unknown as number, {
        locale: locale?.dateLocale ?? TimestampsSettings.defaultDateLocale?.dateLocale,
        zone: locale?.timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone
    }));
}

/**
 * Transform a timestamp in TsDate if needed
 * @param ts Timestamp or TsDate
 * * locale is used only if ts is a Timestamp (we don't convert the TsDate to a new locale)
 */
export function asDate(ts: TsParam, locale?: Partial<DateLocale>): TsDate {
    if (typeof ts === "number") {
        return toDate(ts, locale);
    } else {
        return ts as TsDate;
    }
}

/** Convert date from jsDate */
export function fromJSDate(date: Date, locale?: Partial<DateLocale>): TsDate {
    return _td(DateTime.fromJSDate(date, { zone: locale?.timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone }));
}

/** Get js date from UnixTimestamp. Main purpose : excel export */
export function toJsDate(ts: UnixTimestamp, locale?: Partial<DateLocale>): Date {
    return DateTime.fromSeconds(ts, { zone: locale?.timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone }).toJSDate();
}

/**
 * Created a TsDate from a date object
 */
export function fromDateObject(obj: Partial<TsDateObjectValues>, locale?: Partial<DateLocale>): TsDate {
    // handle Cervval.js additional parameters (that are not handled by luxon)
    let secondOfDay = obj.secondOfDay;
    const minuteOfDay = obj.minuteOfDay;
    if (secondOfDay != null || minuteOfDay != null) {
        // remove additional properties that will be handled later
        const cleanObj = { ...obj };
        delete cleanObj.secondOfDay;
        delete cleanObj.minuteOfDay;
        obj = cleanObj;
    }

    let result = _td(DateTime.fromObject(obj, {
        locale: locale?.dateLocale ?? TimestampsSettings.defaultDateLocale?.dateLocale,
        zone: locale?.timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone
    }));

    // handle additional properties

    if (secondOfDay == null && minuteOfDay == null && obj.hour == null && obj.minute == null && obj.second == null && _configurationBeforeOverrideBeginOfDay?.overriddenSecondOfDay) {
        // set "begin of day" seconds
        secondOfDay = _configurationBeforeOverrideBeginOfDay.overriddenSecondOfDay;
    }
    if (secondOfDay != null) {
        result = set(result, { secondOfDay });
    } else if (minuteOfDay != null) {
        result = set(result, { minuteOfDay });
    }

    return result;
}


const indexOfDayUnit = DATE_UNITS.indexOf("day");
/**
 * @returns true if unit is day or above (week, month, year)
 */
function _isDayOrAboveUnit(unit: TsDateUnit): boolean {
    const indexOfUnit = DATE_UNITS.indexOf(unit);
    return indexOfUnit <= indexOfDayUnit;
}

// save config to be able to restore it
let _configurationBeforeOverrideBeginOfDay: {
    beginOf: typeof _additionalProperties.beginOf,
    beginOfNext: typeof _additionalProperties.beginOfNext,
    beginOfPrevious: typeof _additionalProperties.beginOfPrevious,
    endOf: typeof _asideProperties.endOf,
    overriddenSecondOfDay: number
} | undefined = undefined;

/**
 * override the beginOf and associate functions so that the begin of day corresponds to the provided secondOfDay
 * @param secondOfDay
 */
export function overrideBeginOfDay(secondOfDay: number): void {
    clearTimestampsCache();
    if (_configurationBeforeOverrideBeginOfDay == null) {
        // save config
        _configurationBeforeOverrideBeginOfDay = {
            beginOf: _additionalProperties.beginOf,
            beginOfNext: _additionalProperties.beginOfNext,
            beginOfPrevious: _additionalProperties.beginOfPrevious,
            endOf: _asideProperties.endOf,
            overriddenSecondOfDay: secondOfDay
        };
    } else {
        _configurationBeforeOverrideBeginOfDay.overriddenSecondOfDay = secondOfDay;
    }
    if (secondOfDay === 0) {
        if (_configurationBeforeOverrideBeginOfDay != null) {
            // restore defaults
            _additionalProperties.beginOf = _configurationBeforeOverrideBeginOfDay.beginOf;
            _additionalProperties.beginOfNext = _configurationBeforeOverrideBeginOfDay.beginOfNext;
            _additionalProperties.beginOfPrevious = _configurationBeforeOverrideBeginOfDay.beginOfPrevious;
            _asideProperties.endOf = _configurationBeforeOverrideBeginOfDay.endOf;
        }
        // do not override
        return;
    }
    _additionalProperties.beginOf = (dt: DateTime) => (unit: TsDateUnit): TsDate => {
        if (_isDayOrAboveUnit(unit)) {
            let result = _td(dt.startOf(unit)).setValues({ secondOfDay });
            if (_dt(result).toSeconds() > dt.toSeconds()) {
                // if startOf is after dt then we need to go to "previous" start
                result = result.subtract({ unit, unitCount: 1 });
            }
            return result;
        } else {
            return _td(dt.startOf(unit));
        }
    };

    _additionalProperties.beginOfNext = (dt: DateTime) => (unit: TsDateUnit): TsDate => {
        return _additionalProperties.beginOf(dt)(unit).add({ unit, unitCount: 1 });
    };

    _additionalProperties.beginOfPrevious = (dt: DateTime) => (unit: TsDateUnit): TsDate => {
        return _additionalProperties.beginOf(dt)(unit).subtract({ unit, unitCount: 1 });
    };

    _asideProperties.endOf = (dt: DateTime) => (unit: TsDateUnit): TsDate => {
        return _td(_dt(_additionalProperties.beginOf(dt)(unit)).plus({ day: 1, milliseconds: -1 }));
    };

}

//#region internal object conversion

/**
 * @returns luxon DateTime from TsDate
 */
function _dt(td: TsParam): DateTime {
    if (typeof td === "number") {
        return DateTime.fromSeconds(td);
    }
    return td as unknown as DateTime;
}

/** Get additional property value (or function) */
const _additionalProperties = {
    // ! names in _additionalProperties must be different from luxon internal properties
    uts: (dt: DateTime) => Math.floor(dt.toSeconds()),
    epochSeconds: (dt: DateTime) => Math.floor(dt.toSeconds()),
    epochMillis: (dt: DateTime) => dt.toMillis(),

    timeZone: (dt: DateTime) => dt.zoneName,
    secondOfDay: (dt: DateTime) => dt.second + dt.minute * SECONDS_IN_ONE_MINUTE + dt.hour * SECONDS_IN_ONE_HOUR,
    minuteOfDay: (dt: DateTime) => dt.minute + dt.hour * MINUTES_IN_ONE_HOUR,
    yearAndOrdinal: (dt: DateTime) => dt.year * 1000 + dt.ordinal,
    yearAndMonth: (dt: DateTime) => dt.year * 100 + dt.month,
    weekYearAndWeekNumber: (dt: DateTime) => dt.weekYear * 100 + dt.weekNumber,

    // functions
    setValues: (dt: DateTime) => (values: Partial<TsDateObjectValues>): TsDate => set(_td(dt), values),
    add: (dt: DateTime) => (duration: TsDurationParam): TsDate => _td(dt.plus(_d(duration))),
    subtract: (dt: DateTime) => (duration: TsDurationParam): TsDate => _td(dt.minus(_d(duration))),
    beginOf: (dt: DateTime) => (unit: TsDateUnit): TsDate => _td(dt.startOf(unit)),
    beginOfNext: (dt: DateTime) => (unit: TsDateUnit): TsDate => _td(dt.startOf(unit)).add({ unit, unitCount: 1 }),
    beginOfPrevious: (dt: DateTime) => (unit: TsDateUnit): TsDate => _td(dt.startOf(unit)).subtract({ unit, unitCount: 1 })
    // BGS there is no endOf on purpose, most of the time we should use beginOfNext
};

// endOf is not within _additionalProperties but we want to be able to override it
const _asideProperties = {
    // luxon endof is slow => we use startOf instead
    endOf: (dt: DateTime) => (unit: TsDateUnit): TsDate => _td(dt.startOf(unit).plus({ [unit]: 1, milliseconds: -1 }))
};

/**
 * @returns TsDate from luxon DateTime
 */
function _td(dt: DateTime): TsDate {

    if (getProp(dt, IS_TS_DATE_PROPERTY)) { return dt as unknown as TsDate; }

    // build proxy
    return new Proxy({
        [IS_TS_DATE_PROPERTY]: true,
        __dt: dt
    }, {
        get: function (obj: { [IS_TS_DATE_PROPERTY]: true, __dt: DateTime }, prop: string) {
            // Get property value (or function) from DateTime
            let result = getProp(obj.__dt, prop);
            if (result === undefined) {
                // handle additional properties
                const propFunc = getProp<(dt: DateTime) => unknown>(_additionalProperties, prop);
                if (propFunc) {
                    result = propFunc(obj.__dt);
                }
            }
            return result;
        }
    }) as unknown as TsDate;
}

//#endregion internal object conversion

//#region Date manipulation

/**
 * "Set" the values of specified units.
 *
 * set(date,{ year: 2017 })
 * set(date,{ hour: 8, minute: 30 })
 * set(date,{ weekday: 5 })
 * set(date,{ year: 2005, ordinal: 234 })
 * 
 * @returns a newly-constructed TsDate.
 */
export function set(td: TsParam, values: Partial<TsDateObjectValues>): TsDate {
    // handle Cervval.js additional parameters (that are not handled by luxon)
    const secondOfDay = values.secondOfDay;
    const minuteOfDay = values.minuteOfDay;
    if (secondOfDay != null || minuteOfDay != null) {
        // remove additional properties that will be handled later
        const cleanValues = { ...values };
        delete cleanValues.secondOfDay;
        delete cleanValues.minuteOfDay;
        values = cleanValues;
    }

    // set values with luxon
    let dt = _dt(td).set(values);

    // handle additional properties
    if (secondOfDay != null) {
        // set seconds of day
        dt = dt.set({
            hour: Math.trunc(secondOfDay / SECONDS_IN_ONE_HOUR),
            minute: Math.trunc(secondOfDay % SECONDS_IN_ONE_HOUR / SECONDS_IN_ONE_MINUTE),
            second: secondOfDay % SECONDS_IN_ONE_MINUTE,
            millisecond: 0
        });
    } else if (minuteOfDay != null) {
        // set minutes of day
        dt = dt.set({
            hour: Math.trunc(minuteOfDay / MINUTES_IN_ONE_HOUR),
            minute: minuteOfDay % MINUTES_IN_ONE_HOUR,
            second: 0,
            millisecond: 0
        });
    }
    return _td(dt);
}

/**
 * @returns the value of specified unit
 * 
 * set(date,"year") => 2021
 */
export function get(td: TsParam, unit: TsDateUnit): number {

    const value = getProp<number>(_dt(td), unit);
    return value;
}

/**
 * Adding hours, minutes, seconds, or milliseconds increases the timestamp by the right number of milliseconds. 
 * Adding days, months, or years shifts the calendar, accounting for DSTs and leap years along the way.
 * Thus, `add(ts,{ hours: 24 })` may result in a different time than `add(ts,{ days: 1 })` if there's a DST shift in between.
 *
 * add(date, { minutes: 15 }) //~> in 15 minutes
 * add(date, { days: 1 }) //~> this time tomorrow
 * add(date, { days: -1 }) //~> this time yesterday
 * add(date, { hours: 3, minutes: 13 }) //~> in 3 hr, 13 min
 * add(date, { hours: 3, minutes: - 3 }) //~> in 2 hr, 57 min
 * 
 * @returns a newly-constructed TsDate.
 */
export function add(td: TsParam, duration: TsDurationParam): TsDate {
    return _td(_dt(td).plus(_d(duration)));
}

/**
 * See {@link add}
 */
export function subtract(td: TsParam, duration: TsDurationParam): TsDate {
    return _td(_dt(td).minus(_d(duration)));
}

/**
 * "Build" a TsDate corresponding to the beginning of a unit of time from passed date
 * 
 * beginOf('2021-02-27 17:22:12', 'hour'); //=> '2021-02-27 17:00:00'
 * 
 * @returns a newly-constructed TsDate.
 */
export function beginOf(td: TsParam, unit: TsDateUnit): TsDate {
    return _td(_dt(td)).beginOf(unit);
}

/**
 * "Build" a TsDate corresponding to the NEXT beginning of a unit of time from passed date.
 * 
 * beginOfNext('2021-02-27 17:22:12', 'hour'); //=> '2021-02-27 18:00:00'
 * 
 * @returns a newly-constructed TsDate.
 */
export function beginOfNext(td: TsParam, unit: TsDateUnit): TsDate {
    return _td(_dt(td)).beginOfNext(unit);
}

/**
 * "Build" a TsDate corresponding to the PREVIOUS beginning of a unit of time from passed date.
 * 
 * beginOfPrevious('2021-02-27 17:22:12', 'day'); //=> '2021-02-26 00:00:00'
 * 
 * @returns a newly-constructed TsDate.
 */
export function beginOfPrevious(td: TsParam, unit: TsDateUnit): TsDate {
    return _td(_dt(td)).beginOfPrevious(unit);
}

/**
 * "Build" a TsDate corresponding to the NEXT beginning of a unit of time from passed date.
 * 
 * ! In Zurvan Planning applications, when computing the finish time of an element most of the time you should use beginOfNext and not endOf
 * 
 * endOf('2021-02-27 17:22:12', 'hour'); //=> '2021-02-27 17:59:59'
 * 
 * @returns a newly-constructed TsDate.
 */
export function endOf(td: TsParam, unit: TsDateUnit): TsDate {
    return _asideProperties.endOf(_dt(td))(unit);
}

/**
 * Return the difference corresponding to specified unit between two timestamps.
 * diff(1,2,"second") => 1
 * diff(2,1,"second") => -1
 */
export function diff(intervalStart: TsParam, intervalFinish: TsParam, unit: TsDateUnit): number {
    const result = _dt(intervalFinish).diff(_dt(intervalStart), [unit]);
    return result.as(unit);
}

/**
 * Return the rounded difference corresponding to specified unit between two timestamps.
 * diff(1,2,"second") => 1
 * diff(2,1,"second") => -1
 */
export function diffRound(intervalStart: TsParam, intervalFinish: TsParam, unit: TsDateUnit): number {
    return Math.round(diff(intervalStart, intervalFinish, unit));
}

/**
 * areSame(t1,t2,"day") => true if t1 and t2 have the same year month and day [in t1 timezone]
 * areSame(t1,t2,"hour") => true if t1 and t2 have the same year month day and hour [in t1 timezone]
 * areSame(t1,t2) => true if t1 === t2 [in t1 timezone]
 */
export function areSame(t1: TsParam, t2: TsParam, untilUnit?: TsDateUnit): boolean {

    // If same object/value, return true
    if (t1 === t2) { return true; }

    const d1 = _dt(t1);
    let d2 = _dt(t2);
    if (d1.zoneName !== d2.zoneName && d1.zoneName) {
        // ensure they are both in the same time zone
        d2 = d2.setZone(d1.zoneName);
    }

    if (untilUnit == null || untilUnit === "second") {
        // no unit param => compare timestamp globally
        return d1.toSeconds() === d2.toSeconds();
    }

    // DATE_UNITS contains the units sorted by importance
    for (const u of DATE_UNITS) {
        if (getProp(d1, u) !== getProp(d2, u)) {
            return false;
        }
        if (untilUnit === u) {
            // we have reached the target unit => we stop the comparison
            break;
        }
    }
    return true;
}
/**
 * Get all days that cover the provided interval
 * If options.strict is true and the to parameter is on midnight, the "to" day is not included
 * "2022-03-01 18:00", "2022-03-03 18:00" => 2022-03-01, 2022-03-02, 2022-03-03
 * "2022-03-01 18:00", "2022-03-03 00:00" => if (strict) 2022-03-01, 2022-03-02 otherwise 2022-03-01, 2022-03-02, 2022-03-03
 * "2022-03-01 18:00", "2022-03-01 19:00" => 2022-03-01
 */
export function getIntersectingDays(from: TsParam, to: TsParam, options: { strict: boolean }): TsDate[] {
    if (tsValueOf(from) > tsValueOf(to)) {
        // reverse
        return getIntersectingDays(to, from, options);
    }
    const result: TsDate[] = [];
    const end = _td(_dt(to));
    let current = beginOf(from, "day");
    while (current.uts <= end.uts) {
        if (options.strict && current.uts === end.uts) {
            // strict => ignore finish
            break;
        }
        result.push(current);
        current = add(current, { day: 1 });
    }
    return result;
}

/** build TsDateInterval from TsInterval */
export function toTsDateInterval(tsInterval: TsInterval): TsDateInterval {
    return {
        start: asDate(tsInterval.from),
        finish: asDate(tsInterval.to)
    };
}

/** build TsInterval from TsDateInterval */
export function toTsInterval(tsDateInterval: TsDateInterval): TsInterval {
    return {
        from: tsDateInterval.start.uts,
        to: tsDateInterval.finish.uts
    };
}

/**
 * Get all days that cover the provided interval
 * If options.strict is true and the to parameter is on midnight, the "to" day is not included
 * "2022-03-01 18:00", "2022-03-03 18:00" => 2022-03-01, 2022-03-02, 2022-03-03
 * "2022-03-01 18:00", "2022-03-03 00:00" => if (strict) 2022-03-01, 2022-03-02 otherwise 2022-03-01, 2022-03-02, 2022-03-03
 * "2022-03-01 18:00", "2022-03-01 19:00" => 2022-03-01
 * @returns {start : startOfDay, finish: startOfNextDay} for each intersecting day
 */
export function getIntersectingDayIntervals(from: TsParam, to: TsParam, options: { strict: boolean }): TsDateInterval[] {
    const days = getIntersectingDays(from, to, options);
    const result: TsDateInterval[] = [];
    for (let i = 0; i < days.length - 1; i++) {
        const interval: TsDateInterval = { start: days[i], finish: days[i + 1] };
        result.push(interval);
    }
    if (days.length > 0) {
        // compute last day interval
        const lastDay = days[days.length - 1];
        const interval: TsDateInterval = { start: lastDay, finish: lastDay.add({ day: 1 }) };
        result.push(interval);
    }
    return result;
}

//#endregion Date manipulation

//#region Calendar infos
/**
 * Returns true if this TsDate is in a leap year, false otherwise
 *
 * isInLeapYear(fromDateObject({year:2016})) //=> true
 * isInLeapYear(fromDateObject({year:2013})) //=> false
 */
export function isInLeapYear(td: TsParam): boolean {
    return _dt(td).isInLeapYear;
}

/**
 * Returns the number of days in this TsDate's month
 *
 * daysInMonth(fromDateObject({year:2016, month:2})) //=> 29
 * daysInMonth(fromDateObject({year:2016, month:3})) //=> 31
 */
export function daysInMonth(td: TsParam): number {
    return _dt(td).daysInMonth!;// Assume than td params is always a valid date
}

/**
 * Returns the number of days in this TsDate's year
 *
 * daysInYear(fromDateObject({year:2016, month:2})) //=> 366
 * daysInYear(fromDateObject({year:2013, month:2})) //=> 365
 */
export function daysInYear(td: TsParam): number {
    return _dt(td).daysInYear;
}

/**
 * Returns the number of weeks in this TsDate's year
 * @see https://en.wikipedia.org/wiki/ISO_week_date
 *
 * weeksInWeekYear(fromDateObject({year:2004, month:2})) //=> 53
 * weeksInWeekYear(fromDateObject({year:20013, month:2})) //=> 52
 */
export function weeksInWeekYear(td: TsParam): number {
    return _dt(td).weeksInWeekYear;
}

//#endregion Calendar infos


//#region Format / Parse

/** 
 * Format the Timestamp with format string or locale dependant Intl.DateTimeFormatOptions
 */
export function format(td: TsParam, formatOptions: TsDateFormat, localeOptions?: Partial<DateLocale>): string {

    const locale = localeOptions?.dateLocale ?? TimestampsSettings.defaultDateLocale?.dateLocale;
    const zone = localeOptions?.timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone;

    if (!TimestampsSettings.disableFormatCache && typeof formatOptions === "string") {
        const tdUts = tsParamUts(td);

        clearTimestampsCacheIfExpired();
        const cacheKey = tdUts + "-" + locale + "-" + zone + "@" + formatOptions;
        let result = _cache.formatCache[cacheKey];
        if (result == null) {
            result = _formatImpl(td, formatOptions, locale, zone);
            _cache.formatCache[cacheKey] = result;
        }
        return result;
    }
    return _formatImpl(td, formatOptions, locale, zone);
}

/**
 * @deprecated do not call directly since it does not handle defaultDateLocale, call format
 */
function _formatImpl(td: TsParam, formatOptions: TsDateFormat, locale: LocaleString | undefined, zone: TimeZoneString | typeof UTC_TZ | undefined): string {
    let dt = _dt(td);
    if (zone != null) {
        dt = dt.setZone(zone);
    }
    // formatOptions is a format string
    if (typeof formatOptions === "string") {
        return dt.toFormat(formatOptions, { locale });
    }
    // formatOptions is a Intl.DateTimeFormatOptions
    return dt.toLocaleString(formatOptions, { locale });
}

/** Format a FINISH_DATE Timestamp 
 * Use this format function when you want to display a finishDate (in itemFormatter for example)
 */
export function formatFinishDate(td: TsParam, formatString: TsDateFormat, locale?: Partial<DateLocale>): string {
    const val = subtract(td, { day: 1 });
    return format(val, formatString, locale);
}

export type FormatIntervalDecorators = { from: string, to: string, on: string, since: string };
export const DEBUG_INTERVAL_DECORATORS = { from: "", to: "=>", on: "", since: ">" };

/**
 * Get decorators for provided locale (or default locale if none provided)
 */
export function getFormatIntervalDecorators(locale?: Partial<DateLocale>): FormatIntervalDecorators {
    const l = locale?.dateLocale ?? TimestampsSettings.defaultDateLocale?.dateLocale;
    // TODO use i18n file
    if (l?.startsWith("fr")) {
        return { from: "du ", to: " au ", on: "le ", since: "à partir du " };
    }
    return { from: "from ", to: " to ", on: "on ", since: "since " };
}


/** Format the Timestamp interval */
function _formatInterval(start: TsParam, finish: TsParam | undefined, formatStartStr: TsDateFormat, formatFinishStr: TsDateFormat, formatAsFinishDate: boolean, decorators?: FormatIntervalDecorators, locale?: Partial<DateLocale>): string {
    if (decorators == null) {
        decorators = getFormatIntervalDecorators(locale);
    }
    const startStr = format(start, formatStartStr, locale);
    if (finish != null) {
        const finishStr = formatAsFinishDate ? formatFinishDate(finish, formatFinishStr, locale) : format(finish, formatFinishStr, locale);
        // ! note that we may have different timestamps with the same formatted strings (for example different seconds)
        if (startStr === finishStr) {
            return decorators.on + startStr;
        }
        return decorators.from + startStr + decorators.to + finishStr;
    } else {
        return decorators.since + startStr;
    }
}

/** Format the Timestamp interval */
export function formatInterval(start: TsParam, finish: TsParam | undefined, formatStr: TsDateFormat, decorators?: FormatIntervalDecorators, locale?: Partial<DateLocale>, options?: { isADateInterval: boolean }): string {
    return _formatInterval(start, finish, formatStr, formatStr, Boolean(options?.isADateInterval), decorators, locale);
}

/** Format the Timestamp interval but assumes finish date is not included and displays the previous day (calls formatFinishDate) */
export function formatDateInterval(start: TsParam, finish: TsParam | undefined, formatStr: TsDateFormat, decorators?: FormatIntervalDecorators, locale?: Partial<DateLocale>): string {
    return formatInterval(start, finish, formatStr, decorators, locale, { isADateInterval: true });
}

/** 
 * format start date with formatDate if it is on midnight and there is at least one day between start and finish, with formatDateTime otherwise 
 * format finish date as a finishDate with formatDate it is on midnight and there is at least one day between start and finish, with formatDateTime otherwise
 */
export function formatHumanReadableInterval(start: TsParam, finish: TsParam, format?: { formatDate: TsDateFormat, formatDateTime: TsDateFormat }, decorators?: FormatIntervalDecorators, locale?: Partial<DateLocale>): string {
    if (format == null) {
        format = {
            formatDate: DEFAULT_DATE_FORMAT,
            formatDateTime: DEFAULT_DATETIME_FORMAT
        };
    }
    start = asDate(start);
    finish = asDate(finish);

    const isMoreThanOneDay = diff(start, finish, "day") >= 1;

    const overriddenSecondOfDay = _configurationBeforeOverrideBeginOfDay?.overriddenSecondOfDay;

    const isStartTimeStartOfDay = start.secondOfDay === (overriddenSecondOfDay ?? 0);
    const isFinishTimeStartOfDay = finish.secondOfDay === (overriddenSecondOfDay ?? 0);

    // format start date with formatDateStr if it is on midnight and there is at least one day between start and finish, with formatDateTimeStr otherwise 
    const formatStart = isStartTimeStartOfDay && isMoreThanOneDay ? format.formatDate : format.formatDateTime;

    let formatFinish: TsDateFormat;
    let formatAsFinishDate: boolean;

    if (isFinishTimeStartOfDay && isMoreThanOneDay) {
        // format finish date as a finishDate with formatDateStr it is on midnight and there is at least one day between start and finish
        formatFinish = format.formatDate;
        formatAsFinishDate = true;
    } else {
        // format with formatDateTimeStr otherwise
        formatFinish = format.formatDateTime;
        formatAsFinishDate = false;
    }
    return _formatInterval(start, finish, formatStart, formatFinish, formatAsFinishDate, decorators, locale);
}

/** clear timestamps cache */
export function clearTimestampsCache(): void {
    _cache.lastClearMs = Date.now();
    _cache.parseCache = {};
    _cache.formatCache = {};
}

/** clear timestamps cache if it has expired*/
export function clearTimestampsCacheIfExpired(): void {
    if (Date.now() - _cache.lastClearMs > TimestampsSettings.cacheExpiresAfterSeconds * 1000) {
        // please leave the console it will help us know how many items we get in the cache and improve cache handling
        const formatKeyCount = Object.keys(_cache.formatCache).length;
        const parseKeyCount = Object.keys(_cache.parseCache).length;
        if (formatKeyCount > 0 || parseKeyCount > 0) {
            console.info("Timestamp cache expired - cleared with " + formatKeyCount + " formats and " + parseKeyCount + " parses");
        }
        clearTimestampsCache();
    }
}

const _cache = {
    lastClearMs: 0,
    parseCache: {} as Record<string, TsDate>,
    formatCache: {} as Record<string, string>
};

/**
 * @returns options restricted to LooseParsingOptions properties
 */
export function extractLooseParsingOptions(options: LooseParsingOptions | undefined): LooseParsingOptions | undefined {
    if (options == null) { return undefined; }
    return { doNotConvert2DigitsYear: options?.doNotConvert2DigitsYear, doNotTryLooseParsing: options?.doNotTryLooseParsing };
}

/** 
 * Parse the Timestamp
 * * Loose parsing is enabled via LOOSE_PARSE_OPTIONS (by default for DEFAULT_DATE_FORMAT, DEFAULT_DATETIME_FORMAT and DEFAULT_HOUR_MINUTE_FORMAT only)
 * * On DST change hours, if the date can be interpreted as two UTC timestamps, will return the soonest.
 * 
 * If doNotConvert2DigitsYear is NOT set to true a year of 25 will be transformed to 2025 (00 to 60 => 20xx, 61 to 99 => 19xx)
 * Note that even if doNotConvert2DigitsYear is false, for loose year parsing to work you need to have a compatible format string (default is ok)
 */
export function parse(str: string, formatStr: string, localeOptions?: Partial<DateLocale> & LooseParsingOptions): TsDate {
    const locale = localeOptions?.dateLocale ?? TimestampsSettings.defaultDateLocale?.dateLocale;
    const zone = localeOptions?.timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone;
    if (!TimestampsSettings.disableParseCache) {
        clearTimestampsCacheIfExpired();
        const cacheKey = str + "-" + locale + "-" + zone + "@" + formatStr;
        let result = _cache.parseCache[cacheKey];
        if (result == null) {
            result = _parseImpl(str, formatStr, locale, zone, extractLooseParsingOptions(localeOptions));
            _cache.parseCache[cacheKey] = result;
        }
        return result;
    }
    return _parseImpl(str, formatStr, locale, zone, extractLooseParsingOptions(localeOptions));
}

/**
 * @deprecated do not call directly since it does not handle defaultDateLocale, call parse
 */
function _parseImpl(str: string, formatStr: string, dateLocale: LocaleString | undefined, timeZone: TimeZoneString | typeof UTC_TZ | undefined, options?: LooseParsingOptions): TsDate {

    if (!options?.doNotTryLooseParsing) {
        // Loose parsing : allow user to input approximate format "03/08/2022 01:02", "3/8/2022 1:2", "3/8/22 1:2"
        const looseFormat: string = getProp<anyObject>(LOOSE_PARSE_OPTIONS, String(dateLocale))?.[formatStr];
        if (looseFormat != null) {
            formatStr = looseFormat;
        }
    }

    // Parse
    let dt = DateTime.fromFormat(str, formatStr, { locale: dateLocale, zone: timeZone });

    // Handle Daylight Saving Time to always get the soonest value
    // eg 2021-10-31 02:00:00 (Europe/Paris) can be either 1635638400 (31/10/2021 00:00:00, UTC) or 1635642000 (31/10/2021 01:00:00, UTC)
    // Luxon does not guarantee which timestamp is returned : https://github.com/moment/luxon/blob/master/docs/zones.md#ambiguous-times
    if (dt.isValid && !dt.isInDST) {
        // If not in DST, check if timestamp one hour earlier is the same hour ...
        // Won't work in "Australia: Lord Howe Island" timezone which has a 30 minute DST diff, add a parameter when we get a contract here.
        const soonest = dt.minus({ hour: 1 });
        if (soonest.hour === dt.hour) {
            // ... if it happens to be the case, we want the soonest hour
            dt = soonest;
        }
    }

    // Loose parsing, year handling
    if (!options?.doNotConvert2DigitsYear && dt.isValid && dt.year >= 0 && dt.year < 100) {
        // if year is between 0 and 99 we adjust the year
        // luxon format "yy" : two-digit year, interpreted as > 1960
        dt = dt.plus({ year: dt.year > 60 ? 1900 : 2000 });
    }

    const td = _td(dt);
    // add overrideBeginOfDay option
    if (_configurationBeforeOverrideBeginOfDay?.overriddenSecondOfDay) {
        if (td.secondOfDay === 0) {
            if (formatStr.includes("t") || formatStr.includes("H") || formatStr.includes("h")) {
                // format contains a time => no override of secondOfDay
            } else {
                return td.setValues({ secondOfDay: _configurationBeforeOverrideBeginOfDay?.overriddenSecondOfDay });
            }
        }
    }
    return td;
}

/**
 * Create a TsDate from an ISO 8601 string
 *
 * @param str - the ISO string
 * @param locale - DateLocale and TimeZone to set on the resulting instance.TimeZone is used if no offset is specified in the input string itself.
 */
export function parseISO(str: string, locale?: Partial<DateLocale>): TsDate {
    return _td(DateTime.fromISO(str, {
        locale: locale?.dateLocale ?? TimestampsSettings.defaultDateLocale?.dateLocale,
        zone: locale?.timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone
    }));
}

/**
 * Format the TsParam to ISO 8601 
 * @param td date or timestamp to format
 * @param timeZone timeZone to use to display time
 * @returns the string representing the date conforming to ISO 8601 formatting ("yyyy-MM-dd'T'HH:mm:ss.SSSZZ"=>"2019-09-01T14:28:00.000+02:00")
 */
export function toISO(td: TsParam, timeZone?: string): string {
    const zone = timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone;
    let dt = _dt(td);
    if (zone != null) {
        dt = dt.setZone(zone);
    }
    return dt.toISO()!;// Assume than td params is always a valid date
}

/**
 * Create a TsDate from an SQL string
 *
 * @param str - the SQL string
 */
export function parseSQL(str: string): TsDate {
    if (!TimestampsSettings.disableParseCache) {
        clearTimestampsCacheIfExpired();
        const cacheKey = str + "@sql";
        let result = _cache.parseCache[cacheKey];
        if (result == null) {
            result = _td(DateTime.fromSQL(str, { zone: UTC_TZ }));
            _cache.parseCache[cacheKey] = result;
        }
        return result;
    }
    return _td(DateTime.fromSQL(str, { zone: UTC_TZ }));
}

/**
 * Format the TsParam to SQL
 */
export function _toSqlImpl(td: TsParam, zone: string | undefined): string {
    let dt = _dt(td);
    if (zone != null) {
        dt = dt.setZone(zone);
    }
    return dt.toSQL({ includeOffset: false })!;// Assume than td params is always a valid date
}

/**
 * Format the TsParam to SQL
 * @param td date or timestamp to format
 * @param timeZone timeZone to use to display time
 * @returns the string representing the date in SQL formatting ("yyyy-MM-dd HH:mm:ss.SSSZZ"=>"2019-09-01 14:28:00.000+02:00")
 */
export function toSQL(td: TsParam, timeZone?: string): string {

    const zone = timeZone ?? TimestampsSettings.defaultDateLocale?.timeZone;

    if (!TimestampsSettings.disableFormatCache) {
        clearTimestampsCacheIfExpired();
        const tdUts = tsParamUts(td);
        const cacheKey = tdUts + "-" + zone + "@sql";
        let result = _cache.formatCache[cacheKey];
        if (result == null) {
            result = _toSqlImpl(td, zone);
            _cache.formatCache[cacheKey] = result;
        }
        return result;
    }
    return _toSqlImpl(td, zone);
}

//#endregion Format / Parse


//#region Duration


/**
 * Convert TsSimpleDuration to TsDuration
 */
export function toDuration(duration: TsSimpleDuration): TsDuration {
    return { [duration.unit]: duration.unitCount };
}

/**
 * Convert TsSimpleDuration to TsDuration if needed
 */
function _d(duration: TsDurationParam): TsDuration {
    const dur = getProp(duration, keyof<TsSimpleDuration>("unit")) ? toDuration(duration as TsSimpleDuration) : duration as TsDuration;
    return dur;
}

/**
 * Get duration value in provided unit
 */
export function durationValue(duration: TsDurationParam, unit: TsDateUnit): number {
    return Duration.fromDurationLike(_d(duration)).as(unit);
}

/** Format duration into human readable string */
export function durationToHuman(duration: TsDurationParam): string {
    return Duration.fromDurationLike(_d(duration)).toHuman();
}

//#endregion Duration

/**
 * Function that can be used to test performance in chrome debugger
 */
export function tesTsDatePerformance(): string {
    let str = "";
    // eslint-disable-next-line no-console
    console.time("tesTsDatePerformance");
    for (let i = 1; i < 10000; i++) {

        const ts = fromDateObject({}).uts;
        let d = toDate(ts);
        d = d.beginOf("day");
        d = d.beginOfNext("day");
        d = d.add({ unit: "minute", unitCount: 1 });
        d = d.add({ day: 1, hour: 1 });

        str = "uts" + d.uts + "timezone" + d.timeZone +
            " yearAndMonth" + d.yearAndMonth + " yearAndOrdinal" + d.yearAndOrdinal + " weekYear" + d.weekYear + " weekNumber" + d.weekNumber + " year" + d.year +
            " ordinal" + d.ordinal + " month" + d.month + " day" + d.day + " weekday" + d.weekday +
            " hour" + d.hour + " minute" + d.minute + " second" + d.second + " secondOfDay" + d.secondOfDay + " minuteOfDay" + d.minuteOfDay;
    }
    // eslint-disable-next-line no-console
    console.timeEnd("tesTsDatePerformance");
    return str;
}
